import { useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Handles the Emergent Google OAuth callback.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Flow:
 *  - User clicks "Continue with Google" → we redirect to auth.emergentagent.com
 *    with `redirect=${origin}/auth/callback`
 *  - Emergent sends them back to /auth/callback#session_id=<id>
 *  - We grab the session_id, exchange it on the backend for a CTP JWT, then
 *    redirect to the dashboard.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : "";

    // Strip the fragment so refreshes don't re-trigger
    if (window.history?.replaceState) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (!sessionId) {
      toast.error("Google sign-in was cancelled.");
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        if (data.token) localStorage.setItem("ctp_token", data.token);
        await refresh();
        toast.success(`Welcome, ${data.user?.name?.split(" ")[0] || "friend"}!`);
        const role = data.user?.role;
        navigate(
          role === "super_admin" || role === "admin" || role === "staff" ? "/admin" : "/dashboard",
          { replace: true }
        );
      } catch (e) {
        const msg = e?.response?.data?.detail || e.message || "Google sign-in failed";
        toast.error(typeof msg === "string" ? msg : "Google sign-in failed");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="w-8 h-8 animate-spin text-[#059669]" />
      <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#525252]">
        Signing you in with Google…
      </div>
    </div>
  );
}
